import type { SurgeProtectionResponse, SurgeProtectionWarning } from "../services/surgeProtection";
import { StatusIcon } from "./StatusIcon";
import { GROUP_TONE } from "./statusTone";

type WarningCode = SurgeProtectionWarning["code"];

// The group values are the shared ones of statusTone.GROUP_TONE, so study.css holds a rule
// and a tone for each: the computed failures under "not-met", the evidence the engineer owes
// and the consistency statements under "review-evidence", the stated items under "not-evaluated".
type WarningGroup = "not-met" | "review-evidence" | "not-evaluated";

interface SurgeProtectionWarningPanelProps {
  result: SurgeProtectionResponse;
}

const NO_WARNINGS_TEXT = "No engineering warnings raised.";

// Exhaustive maps: a new code without a label or a group is a compile error.
const WARNING_CODE_LABELS: Record<WarningCode, string> = {
  PROTECTION_LEVEL_EXCEEDED: "Effective protection level above Uw",
  UC_BELOW_MINIMUM: "Uc below the declared minimum",
  TOV_WITHSTAND_BELOW_REQUIREMENT: "TOV withstand below the requirement",
  IIMP_BELOW_REQUIREMENT: "Iimp below the requirement",
  IN_BELOW_REQUIREMENT: "In below the requirement",
  IMAX_BELOW_REQUIREMENT: "Imax below the requirement",
  SHORT_CIRCUIT_RATING_EXCEEDED: "Short-circuit rating below the prospective current",
  LEAD_VOLTAGE_NOT_ESTABLISHED: "Lead voltage not established",
  UC_MINIMUM_NOT_ESTABLISHED: "Minimum Uc not established",
  TOV_WITHSTAND_NOT_CHECKED: "TOV withstand not checked",
  IIMP_REQUIREMENT_NOT_ESTABLISHED: "Iimp requirement not established",
  IN_REQUIREMENT_NOT_ESTABLISHED: "In requirement not established",
  SPD_TYPE_DOES_NOT_COVER_REQUIREMENT: "Requirement not carried by the SPD type",
  LPS_PRESENCE_UNKNOWN: "Lightning protection system unknown",
  TYPE_1_NOT_DECLARED_AT_ENTRANCE_WITH_LPS: "Type 2 at the service entrance of a structure with an LPS",
  PROSPECTIVE_CURRENT_NOT_ESTABLISHED: "Prospective current not established",
  SHORT_CIRCUIT_RATING_NOT_DECLARED: "Short-circuit rating not declared",
  BACKUP_DEVICE_NOT_DECLARED: "Back-up device not declared",
  PROTECTION_DISTANCE_EXCEEDED: "Beyond the declared protection distance",
  PROTECTION_DISTANCE_NOT_CHECKED: "Protection distance not checked",
  ITEM_DISTANCE_NOT_DECLARED: "Distance from the SPD not declared",
  ENERGY_COORDINATION_NOT_CHECKED: "Energy coordination not checked",
  UP_SOURCE_NOT_DECLARED: "Up source not declared",
  UC_SOURCE_NOT_DECLARED: "Uc or UT source not declared",
  DISCHARGE_SOURCE_NOT_DECLARED: "Discharge parameter source not declared",
  ISCCR_SOURCE_NOT_DECLARED: "Short-circuit rating source not declared",
  PROTECTION_DISTANCE_SOURCE_NOT_DECLARED: "Protection distance source not declared",
  UW_SOURCE_NOT_DECLARED: "Uw source not declared",
  REQUIREMENT_SOURCE_NOT_DECLARED: "Requirement source not declared",
  LEAD_SOURCE_NOT_DECLARED: "Lead source not declared",
  PROSPECTIVE_CURRENT_SOURCE_NOT_DECLARED: "Prospective current source not declared",
  CONNECTION_MODE_SOURCE_NOT_DECLARED: "Connection mode source not declared",
  SYSTEM_VOLTAGE_SOURCE_NOT_DECLARED: "System voltage source not declared",
  LEAD_VOLTAGE_DECLARED_GOVERNS: "Declared lead voltage governs",
  IIMP_PER_MODE_DECLARED: "Iimp declared per mode",
  PROSPECTIVE_CURRENT_DECLARED: "Prospective current declared (not from a stored run)",
  IMAX_NOT_CHECKED: "Imax not compared",
  UP_BASIS_NOT_STATED: "Current at which Up is stated not declared",
  SPD_NECESSITY_NOT_ASSESSED: "Need for an SPD not assessed",
  LPZ_NOT_ASSESSED: "Lightning protection zones not assessed",
  BACKUP_DEVICE_RATING_NOT_CHECKED: "Back-up device rating not checked",
  EARTH_LEAD_SIZE_NOT_CHECKED: "Earth lead size not checked",
  STATUS_INDICATION_NOT_CHECKED: "Status indication not checked",
  COORDINATION_SCOPE_NOT_CHECKED: "Coordination with every other SPD not assessed",
  TYPE_3_AND_DATA_SPD_NOT_COVERED: "Type 3, data and PV DC SPDs not covered",
};

// Where each code is listed: the failed conditions first, then the evidence the engineer owes
// and the consistency statements, then what the sheet states (the F-1 lesson of IR-005: the
// not-checked items are always listed).
const WARNING_CODE_GROUPS: Record<WarningCode, WarningGroup> = {
  PROTECTION_LEVEL_EXCEEDED: "not-met",
  UC_BELOW_MINIMUM: "not-met",
  TOV_WITHSTAND_BELOW_REQUIREMENT: "not-met",
  IIMP_BELOW_REQUIREMENT: "not-met",
  IN_BELOW_REQUIREMENT: "not-met",
  IMAX_BELOW_REQUIREMENT: "not-met",
  SHORT_CIRCUIT_RATING_EXCEEDED: "not-met",
  LEAD_VOLTAGE_NOT_ESTABLISHED: "review-evidence",
  UC_MINIMUM_NOT_ESTABLISHED: "review-evidence",
  TOV_WITHSTAND_NOT_CHECKED: "review-evidence",
  IIMP_REQUIREMENT_NOT_ESTABLISHED: "review-evidence",
  IN_REQUIREMENT_NOT_ESTABLISHED: "review-evidence",
  SPD_TYPE_DOES_NOT_COVER_REQUIREMENT: "review-evidence",
  LPS_PRESENCE_UNKNOWN: "review-evidence",
  TYPE_1_NOT_DECLARED_AT_ENTRANCE_WITH_LPS: "review-evidence",
  PROSPECTIVE_CURRENT_NOT_ESTABLISHED: "review-evidence",
  SHORT_CIRCUIT_RATING_NOT_DECLARED: "review-evidence",
  BACKUP_DEVICE_NOT_DECLARED: "review-evidence",
  PROTECTION_DISTANCE_EXCEEDED: "review-evidence",
  PROTECTION_DISTANCE_NOT_CHECKED: "review-evidence",
  ITEM_DISTANCE_NOT_DECLARED: "review-evidence",
  ENERGY_COORDINATION_NOT_CHECKED: "review-evidence",
  UP_SOURCE_NOT_DECLARED: "review-evidence",
  UC_SOURCE_NOT_DECLARED: "review-evidence",
  DISCHARGE_SOURCE_NOT_DECLARED: "review-evidence",
  ISCCR_SOURCE_NOT_DECLARED: "review-evidence",
  PROTECTION_DISTANCE_SOURCE_NOT_DECLARED: "review-evidence",
  UW_SOURCE_NOT_DECLARED: "review-evidence",
  REQUIREMENT_SOURCE_NOT_DECLARED: "review-evidence",
  LEAD_SOURCE_NOT_DECLARED: "review-evidence",
  PROSPECTIVE_CURRENT_SOURCE_NOT_DECLARED: "review-evidence",
  CONNECTION_MODE_SOURCE_NOT_DECLARED: "review-evidence",
  SYSTEM_VOLTAGE_SOURCE_NOT_DECLARED: "review-evidence",
  LEAD_VOLTAGE_DECLARED_GOVERNS: "not-evaluated",
  IIMP_PER_MODE_DECLARED: "not-evaluated",
  PROSPECTIVE_CURRENT_DECLARED: "not-evaluated",
  IMAX_NOT_CHECKED: "not-evaluated",
  UP_BASIS_NOT_STATED: "not-evaluated",
  SPD_NECESSITY_NOT_ASSESSED: "not-evaluated",
  LPZ_NOT_ASSESSED: "not-evaluated",
  BACKUP_DEVICE_RATING_NOT_CHECKED: "not-evaluated",
  EARTH_LEAD_SIZE_NOT_CHECKED: "not-evaluated",
  STATUS_INDICATION_NOT_CHECKED: "not-evaluated",
  COORDINATION_SCOPE_NOT_CHECKED: "not-evaluated",
  TYPE_3_AND_DATA_SPD_NOT_COVERED: "not-evaluated",
};

const GROUP_ORDER: readonly { group: WarningGroup; title: string }[] = [
  { group: "not-met", title: "SPD check not met" },
  { group: "review-evidence", title: "Review required (evidence)" },
  { group: "not-evaluated", title: "Stated on the sheet (not checked here)" },
];

type ListedWarning = {
  key: string;
  code: WarningCode;
  message: string;
  prefix: string;
};

/** The study's warnings followed by every item's, in the engine's order, items named. */
export function listWarnings(result: SurgeProtectionResponse): ListedWarning[] {
  const study = result.warnings.map((warning, index) => ({
    key: `study-${warning.code}-${index}`,
    code: warning.code,
    message: warning.message,
    prefix: "",
  }));
  const items = result.items.flatMap((item) =>
    item.warnings.map((warning, index) => ({
      key: `${item.code}-${warning.code}-${index}`,
      code: warning.code,
      message: warning.message,
      prefix: `Item ${item.code}: `,
    })),
  );
  return [...study, ...items];
}

export function SurgeProtectionWarningPanel({ result }: SurgeProtectionWarningPanelProps) {
  const warnings = listWarnings(result);

  return (
    <section aria-label="Engineering warnings">
      <h3>Engineering warnings</h3>
      {warnings.length === 0 ? (
        <p data-no-warnings="true">{NO_WARNINGS_TEXT}</p>
      ) : (
        <div data-warning-count={warnings.length}>
          {GROUP_ORDER.map(({ group, title }) => {
            const members = warnings.filter((warning) => WARNING_CODE_GROUPS[warning.code] === group);
            if (members.length === 0) {
              return null;
            }
            return (
              <section key={group} aria-label={title} data-warning-group={group}>
                <h4>
                  <StatusIcon tone={GROUP_TONE[group]} size={20} />
                  {title}
                </h4>
                <ul>
                  {members.map((warning) => (
                    <li key={warning.key} data-warning-code={warning.code}>
                      <strong>
                        {warning.prefix}
                        {WARNING_CODE_LABELS[warning.code]}
                      </strong>
                      {": "}
                      <span>{warning.message}</span>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </section>
  );
}

export { GROUP_ORDER, WARNING_CODE_GROUPS, WARNING_CODE_LABELS };

export default SurgeProtectionWarningPanel;
